import { analytics } from '@helpers/analytics'
import { decode } from '@helpers/shopify'
import { getCookieByName, setCookie } from '@helpers/session'
import trackError from '@helpers/trackError'
import { facebookPixelId, facebookAdClickId } from '@helpers/products'
import { getUserEmail } from '@helpers/session/user'
import { productEventUserProperties } from '@helpers/products'
import { ProductWithVariants } from 'types/product'
import {
  Cart,
  CartDiscountAllocation,
  CartLineItem,
  CartLinesUpdateInput,
  CustomAttribute
} from 'types/checkout'
import { FilterOption } from 'types/filters'
import { EmptyObject } from 'types/helpers'
import { NO_SHOW_ATTRIBUTES_PROPS, ORDERED_OPTIONS } from '@consts/index'
import { getVariantItem } from '../filters'
import {
  getProductVariantAsString,
  getProductCategory,
  getProductSkuProductId,
  getProductSkuGroupId
} from '../products'
import { CartState } from '@store/redux/reducers/cartReducer'

const transformOptionsType = <T extends { type: string }>(options: T[]) =>
  options.map(option =>
    option.type === 'swimshortsleg' ? { ...option, type: 'shortsleg' } : option
  )

function getLineItemInCart(lineItems: CartLineItem[] = [], variantId: number) {
  const lineItem = lineItems.find(lineItem =>
    lineItem.merchandise.id.includes(String(variantId))
  )

  return {
    lineItemVariantId: lineItem?.merchandise?.id,
    lineItemId: lineItem?.id,
    quantityInCart: lineItem?.quantity || 0,
    quantityAvailable: lineItem?.merchandise?.quantityAvailable || 0
  }
}

export function createCartItem(
  product: ProductWithVariants,
  selectedOptions: FilterOption[],
  lineItemsInCart: CartLineItem[] = []
) {
  const newSelectedOptions = transformOptionsType(selectedOptions)
  const newProduct = {
    ...product,
    options: transformOptionsType(product.options)
  }
  const selectedVariants = getVariantItem(newProduct, newSelectedOptions)
  const selectedVariantsWithoutPreorder = selectedVariants.filter(
    variant => variant.pre_order_inventory_quantity === null
  )

  if (!selectedVariantsWithoutPreorder.length) {
    throw new Error('no variants found')
  }

  const { id: variantId, lineItemInCart } = selectedVariantsWithoutPreorder
    .map((variant, index) => {
      const isLastItem = index === selectedVariantsWithoutPreorder.length - 1
      const {
        quantityInCart,
        quantityAvailable,
        lineItemId,
        lineItemVariantId
      } = getLineItemInCart(lineItemsInCart, variant.id)

      if (
        selectedVariantsWithoutPreorder.length > 1 &&
        !isLastItem &&
        lineItemId &&
        quantityInCart >= quantityAvailable
      ) {
        return null
      }

      return {
        ...variant,
        lineItemInCart: {
          quantityInCart,
          quantityAvailable,
          lineItemId,
          lineItemVariantId
        }
      }
    })
    .filter(Boolean)[0]

  const customAttributes: CustomAttribute[] = newSelectedOptions.flatMap(
    option => {
      const key = ORDERED_OPTIONS.find(o => o.toLowerCase() === option.type)
      return key
        ? {
            key,
            value: option.value
          }
        : []
    }
  )

  return {
    lineItemInCart,
    variantId,
    customAttributes
  }
}

export function getCheckoutCookie() {
  return getCookieByName(`checkout_${process.env.SHOP_LOCALE}`)
}

export function getCartCookie() {
  return getCookieByName(`cart_${process.env.SHOP_LOCALE}`)
}

export function setCheckoutCookie({ value = '', expiration = 14 } = {}) {
  setCookie(`checkout_${process.env.SHOP_LOCALE}`, value, expiration)
}

export function setCartCookie({ value = '', expiration = 14 } = {}) {
  setCookie(`cart_${process.env.SHOP_LOCALE}`, value, expiration)
}

function getBasketEventProductData(product: CartLineItem | EmptyObject = {}) {
  const properties: Record<string, string | number> =
    !!product.attributes &&
    product.attributes.reduce(
      (result: Record<string, string | number>, attribute: CustomAttribute) => {
        if (NO_SHOW_ATTRIBUTES_PROPS.includes(attribute.key)) {
          return result
        }

        result[attribute.key.toLowerCase()] =
          Number(attribute.value) || attribute.value
        return result
      },
      {}
    )
  const optionTypes = Object.keys(properties).map(property => ({
    type: property
  }))
  const productVariant = getProductVariantAsString(properties)
  const { title, productType, url } = product.merchandise?.product || {}
  const { image, price, compareAtPrice, sku } = product.merchandise || {}

  return {
    brand: 'SPOKE',
    product_id: getProductSkuProductId({ sku }),
    group_id: getProductSkuGroupId({ sku }),
    category: getProductCategory(optionTypes, productType),
    price: parseFloat(price?.amount) || null,
    ...(parseFloat(compareAtPrice?.amount)
      ? { was_price: parseFloat(compareAtPrice?.amount) }
      : null),
    currency: price?.currencyCode,
    color: title,
    image_url: image?.url,
    quantity: product?.quantity,
    name: productType,
    sku,
    variant: productVariant,
    url,
    ...properties
  }
}

export function cartViewedEvent(checkout: Cart | CartState) {
  const { lines = [], id, cost } = checkout
  const eventProducts = lines.map(item => getBasketEventProductData(item))

  analytics('Cart Viewed', {
    cart_id: id,
    value: Number(cost?.totalAmount?.amount),
    currency: cost?.totalAmount?.currencyCode,
    products: eventProducts
  })
}

function getIsQuantityIncreased(
  product: CartLinesUpdateInput | EmptyObject = {},
  previousItems: CartLineItem[] = []
): boolean {
  return (
    Number(previousItems.find(item => item.id === product.id)?.quantity) <
    product.quantity
  )
}

function getIsQuantityDecreased(
  product: CartLinesUpdateInput | EmptyObject = {},
  previousItems: CartLineItem[] = []
): boolean {
  return (
    Number(previousItems.find(item => item.id === product.id)?.quantity) >
    product.quantity
  )
}

function getCheckoutTotalDiscountCodeValue(
  discountApplications: CartDiscountAllocation[]
) {
  const totalDiscountAmountValues: number[] = []
  discountApplications?.forEach(discount => {
    totalDiscountAmountValues.push(Number(discount.discountedAmount.amount))
  })
  const sumAllDiscountAmountValues = totalDiscountAmountValues.reduce(
    (accumulator, currentValue) => {
      return accumulator + currentValue
    },
    0
  )
  return sumAllDiscountAmountValues
}

function getDiscountCode(discountApplications: CartDiscountAllocation[]) {
  if (!discountApplications) {
    return null
  }

  const filteredDiscountsByCode = discountApplications.filter(discount => {
    if (Object.keys(discount).length > 0 && discount?.code) {
      return discount
    }
  })

  return filteredDiscountsByCode.length > 0
    ? filteredDiscountsByCode[0].code
    : null
}

export function basketEvents({
  checkout = {},
  previousItems = [],
  context = '',
  product = {}
}: {
  checkout: Cart | EmptyObject
  previousItems: CartLineItem[]
  context: string
  product: CartLinesUpdateInput | EmptyObject
}) {
  try {
    const {
      lines = [],
      id,
      checkoutUrl,
      cost: { totalAmount },
      buyerIdentity: { email: checkoutEmail },
      discountAllocations
    } = checkout
    const customerEmail = checkoutEmail || getUserEmail()

    const checkoutDiscountCodeTotalValue =
      getCheckoutTotalDiscountCodeValue(discountAllocations)
    const checkoutDiscountCode = getDiscountCode(discountAllocations)

    const props = {
      ...(customerEmail && { email: customerEmail }),
      ...(checkoutDiscountCode && { coupon: checkoutDiscountCode }),
      ...(checkoutDiscountCodeTotalValue > 0 && {
        discount: checkoutDiscountCodeTotalValue.toFixed(1)
      }),
      context,
      cart_id: id,
      checkout_url: checkoutUrl,
      total: Number(totalAmount?.amount)
    }
    const eventProducts = lines.map(item => getBasketEventProductData(item))

    if (!id) {
      return false
    }

    if (lines.length >= 1 && previousItems.length === 0) {
      const eventProps = {
        ...props,
        products: eventProducts
      }
      analytics('Basket Created', eventProps)
    }

    if (
      lines.length > previousItems.length ||
      getIsQuantityIncreased(product, previousItems)
    ) {
      const eventProduct = lines.find(
        item =>
          decode(item?.merchandise.id).id ===
          decode(product?.merchandiseId || '').id
      )
      const productEventData = getBasketEventProductData(eventProduct)
      const eventProps = {
        ...props,
        ...productEventData,
        ...(!!facebookPixelId && { fbp: facebookPixelId }),
        ...(!!facebookAdClickId && { fbc: facebookAdClickId }),
        ...productEventUserProperties(),
        quantity: 1,
        product_count: lines.length
      }
      analytics('Product Added', eventProps)
    }

    if (
      lines.length < previousItems.length ||
      getIsQuantityDecreased(product, previousItems)
    ) {
      const eventProduct = previousItems.find(
        item => item?.merchandise.id === product.merchandiseId
      )
      const productEventData = getBasketEventProductData(eventProduct)
      const eventProps = {
        ...props,
        ...productEventData,
        ...(lines.length === previousItems.length && { quantity: 1 })
      }
      analytics('Product Removed', eventProps)
    }

    if (lines.length === 0 && previousItems.length > 0) {
      analytics('Basket Emptied', props)
    } else {
      analytics('Basket Updated', {
        ...props,
        products: eventProducts
      })
    }
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : 'basketEvents failed'
    const error = new Error(errorMessage)
    trackError(error, { error })
  }
}

export function getAbTestCheckoutAttributes(
  runningAbTests: Record<string, string>
) {
  if (!runningAbTests) {
    return []
  }

  return Object.keys(runningAbTests).map(testKey => {
    return {
      key: `exp-${testKey}`,
      value: `exp-${testKey}-${runningAbTests[testKey]}`
    }
  })
}
